import React, { useEffect, useState } from 'react';
import { BarChart, Download, ChevronDown, ChevronUp, AlertTriangle, Info, Users, Search, RefreshCw, Calendar, GraduationCap, BookOpen, UserPlus, Clock, Filter, Briefcase, FileText, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { reportsService } from '../../services/reportsService';
import { recruitmentService } from '../../services/recruitmentService';
import { staffService } from '../../services/staffService';
import { intentionsService } from '../../services/intentionsService';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';

interface SubjectQualification {
  id: string;
  subjectCode: string;
  subjectName: string;
  department: string;
  yearLevels: string[];
  requiredQualification: string;
  specialRequirements: string;
}
interface TeacherQualification {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherCode: string;
  qualifications: string[];
  subjects: {
    subjectCode: string;
    subjectName: string;
    yearLevels: string[];
    experienceYears: number;
    isSpecialist: boolean;
  }[];
  department: string;
  fte: number;
  status: 'current' | 'leaving' | 'new' | 'potential';
  contractEndDate?: string;
}
interface SubjectDemand {
  subjectCode: string;
  subjectName: string;
  department: string;
  yearLevel: string;
  sections: number;
  studentsPerSection: number;
  totalStudents: number;
  periodsPerSection: number;
  totalPeriods: number;
  teachersRequired: number;
}
interface StaffingGap {
  subjectCode: string;
  subjectName: string;
  department: string;
  yearLevels: string[];
  teachersRequired: number;
  qualifiedTeachers: number;
  gapFTE: number;
  gapSeverity: 'critical' | 'high' | 'medium' | 'low' | 'none';
  qualifiedStaff: {
    teacherId: string;
    teacherName: string;
    teacherCode: string;
    status: 'current' | 'leaving' | 'new' | 'potential';
    fte: number;
    utilizationPercent: number;
    availableFTE: number;
  }[];
}
interface RecruitmentNeed {
  id: string;
  department: string;
  position: string;
  fte: number;
  subjects: {
    subjectCode: string;
    subjectName: string;
    yearLevels: string[];
  }[];
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'draft' | 'approved' | 'advertised' | 'interviewing' | 'filled';
  notes: string;
  createdDate: string;
  approvedDate?: string;
}
interface DepartmentSummary {
  department: string;
  currentFTE: number;
  requiredFTE: number;
  gapFTE: number;
  teacherCount: number;
  leavingCount: number;
  subjects: {
    subjectCode: string;
    subjectName: string;
    gapFTE: number;
  }[];
}
const RecruitmentGaps: React.FC = () => {
  const {
    addToast
  } = useToast();
  const { learningAreaNames } = useLearningAreas();
  const [subjectQualifications, setSubjectQualifications] = useState<SubjectQualification[]>([]);
  const [teacherQualifications, setTeacherQualifications] = useState<TeacherQualification[]>([]);
  const [subjectDemand, setSubjectDemand] = useState<SubjectDemand[]>([]);
  const [staffingGaps, setStaffingGaps] = useState<StaffingGap[]>([]);
  const [recruitmentNeeds, setRecruitmentNeeds] = useState<RecruitmentNeed[]>([]);
  const [departmentSummaries, setDepartmentSummaries] = useState<DepartmentSummary[]>([]);
  const [selectedGap, setSelectedGap] = useState<string | null>(null);
  const [selectedRecruitment, setSelectedRecruitment] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>('gaps');
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [showNewRecruitmentModal, setShowNewRecruitmentModal] = useState<boolean>(false);
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<string>('gapFTE');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Recruitment planning is forward-looking — default to next year (the year being staffed).
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() + 1);

  // Load real data
  useEffect(() => {
    loadRealData();
  }, [selectedYear]);

  const loadRealData = async () => {
    setIsCalculating(true);
    try {
      // Fetch staffing gaps and summaries from reportsService, plus the raw
      // intentions and staff so we can surface who is not returning next year.
      const [gaps, summaries, recruitNeeds, subjectQuals, intentions, allStaff] = await Promise.all([
        reportsService.calculateStaffingGaps(selectedYear),
        reportsService.calculateDepartmentSummaries(selectedYear),
        recruitmentService.getRecruitmentNeeds(),
        recruitmentService.getSubjectQualifications(),
        intentionsService.getIntentionsByYear(selectedYear),
        staffService.getActiveStaff()
      ]);

      // Leaving staff = teachers whose intention for the year is "not returning".
      // This is real, valuable recruitment signal derived from submitted intentions.
      const staffById = new Map((allStaff || []).map((s: any) => [s.staff_id, s]));
      const leavingStaff: TeacherQualification[] = (intentions || [])
        .filter((i: any) => i.intentionToReturn === 'no')
        .map((i: any) => {
          const s: any = staffById.get(i.staffId);
          return {
            id: i.staffId,
            teacherId: i.staffId,
            teacherName: i.staffName || s?.name || i.staffId,
            teacherCode: i.staffId,
            qualifications: [],
            subjects: (i.teachingPreferences || []).map((p: any) => ({
              subjectCode: '',
              subjectName: p.learningArea,
              yearLevels: [],
              experienceYears: 0,
              isSpecialist: false
            })),
            department: s?.department || i.department || '—',
            fte: s?.fte ?? i.currentFTE ?? 1,
            status: 'leaving' as const,
            contractEndDate: '—'
          };
        });

      // Transform recruitment needs to match component interface
      const transformedNeeds: RecruitmentNeed[] = recruitNeeds.map(need => ({
        id: need.id,
        department: need.department,
        position: need.position_title,
        fte: need.fte,
        subjects: need.subjects || [],
        priority: need.priority,
        status: need.status,
        notes: need.notes || '',
        createdDate: need.created_date,
        approvedDate: need.approved_date
      }));

      // Transform subject qualifications
      const transformedQuals: SubjectQualification[] = subjectQuals.map(q => ({
        id: q.id,
        subjectCode: q.subject_code,
        subjectName: q.subject_name,
        department: q.department,
        yearLevels: q.year_levels,
        requiredQualification: q.required_qualification,
        specialRequirements: q.special_requirements || ''
      }));

      setStaffingGaps(gaps);
      setDepartmentSummaries(summaries);
      setRecruitmentNeeds(transformedNeeds);
      setSubjectQualifications(transformedQuals);
      setTeacherQualifications(leavingStaff);
    } catch (error) {
      console.error('Error loading recruitment data:', error);
      addToast("Couldn't load recruitment data. Please try again.", 'error');
      // Show empty results rather than fabricated sample data.
      setStaffingGaps([]);
      setDepartmentSummaries([]);
      setRecruitmentNeeds([]);
      setSubjectQualifications([]);
      setTeacherQualifications([]);
    } finally {
      setIsCalculating(false);
    }
  };

  // Get filtered staffing gaps
  const getFilteredStaffingGaps = () => {
    return staffingGaps.filter(gap => {
      // Department filter
      if (filterDepartment !== 'all' && displayLearningArea(gap.department) !== filterDepartment) {
        return false;
      }
      // Severity filter
      if (filterSeverity !== 'all' && gap.gapSeverity !== filterSeverity) {
        return false;
      }
      // Search query
      if (searchQuery && !gap.subjectName.toLowerCase().includes(searchQuery.toLowerCase()) && !gap.subjectCode.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort by selected field
      if (sortField === 'subject') {
        return sortDirection === 'asc' ? a.subjectName.localeCompare(b.subjectName) : b.subjectName.localeCompare(a.subjectName);
      } else if (sortField === 'department') {
        return sortDirection === 'asc' ? a.department.localeCompare(b.department) : b.department.localeCompare(a.department);
      } else if (sortField === 'teachersRequired') {
        return sortDirection === 'asc' ? a.teachersRequired - b.teachersRequired : b.teachersRequired - a.teachersRequired;
      } else if (sortField === 'qualifiedTeachers') {
        return sortDirection === 'asc' ? a.qualifiedTeachers - b.qualifiedTeachers : b.qualifiedTeachers - a.qualifiedTeachers;
      } else if (sortField === 'gapFTE') {
        return sortDirection === 'asc' ? a.gapFTE - b.gapFTE : b.gapFTE - a.gapFTE;
      } else if (sortField === 'severity') {
        const severityOrder = {
          critical: 4,
          high: 3,
          medium: 2,
          low: 1,
          none: 0
        };
        return sortDirection === 'asc' ? severityOrder[a.gapSeverity] - severityOrder[b.gapSeverity] : severityOrder[b.gapSeverity] - severityOrder[a.gapSeverity];
      }
      return 0;
    });
  };
  // Get filtered recruitment needs
  const getFilteredRecruitmentNeeds = () => {
    return recruitmentNeeds.filter(need => {
      // Department filter
      if (filterDepartment !== 'all' && displayLearningArea(need.department) !== filterDepartment) {
        return false;
      }
      // Search query
      if (searchQuery && !need.position.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    });
  };
  // Get unique departments
  const getUniqueDepartments = () => {
    const departments = new Set<string>();
    staffingGaps.forEach(gap => {
      departments.add(gap.department);
    });
    return Array.from(departments);
  };
  // Toggle section expansion
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  // Toggle sort direction
  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  // Create new recruitment need
  const createRecruitmentNeed = () => {
    // This would create a new recruitment need in a real application
    addToast('Saving new recruitment needs is coming soon — this was not saved.', 'info');
    setShowNewRecruitmentModal(false);
  };
  // Get severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-danger-bg text-danger-fg">
            Critical
          </span>;
      case 'high':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            High
          </span>;
      case 'medium':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            Medium
          </span>;
      case 'low':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            Low
          </span>;
      case 'none':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            None
          </span>;
      default:
        return null;
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ink-100 text-ink-900">
            Draft
          </span>;
      case 'approved':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            Approved
          </span>;
      case 'advertised':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            Advertised
          </span>;
      case 'interviewing':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            Interviewing
          </span>;
      case 'filled':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            Filled
          </span>;
      default:
        return null;
    }
  };
  // Render teacher status badge
  const getTeacherStatusBadge = (status: string) => {
    switch (status) {
      case 'current':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            Current
          </span>;
      case 'leaving':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-danger-bg text-danger-fg">
            Leaving
          </span>;
      case 'new':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            New
          </span>;
      case 'potential':
        return <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            Potential
          </span>;
      default:
        return null;
    }
  };
  // Render gap details
  const renderGapDetails = (subjectCode: string) => {
    const gap = staffingGaps.find(g => g.subjectCode === subjectCode);
    if (!gap) return null;
    const qualification = subjectQualifications.find(q => q.subjectCode === subjectCode);
    return <Card className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-body-sm font-medium text-ink-700">
            Staffing Gap Details: {gap.subjectName} ({gap.subjectCode})
          </h4>
          <div className="flex items-center space-x-2">
            <div className={`text-card-title ${gap.gapFTE > 0 ? 'text-danger-fg' : 'text-ok-fg'}`}>
              {gap.gapFTE > 0 ? `${gap.gapFTE} FTE Needed` : 'Fully Staffed'}
            </div>
            {getSeverityBadge(gap.gapSeverity)}
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Learning Area</div>
              <div className="text-card-title text-ink-900">
                {displayLearningArea(gap.department)}
              </div>
              <div className="text-meta text-ink-500 mt-1">
                Year Levels: {gap.yearLevels.join(', ')}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Required FTE</div>
              <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
                {gap.teachersRequired.toFixed(1)}
              </div>
              <div className="text-meta text-ink-500 mt-1">
                Qualified Teachers: {gap.qualifiedTeachers}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Gap</div>
              <div className={`text-lg font-medium font-mono tabular-nums ${gap.gapFTE > 0 ? 'text-danger-fg' : 'text-ok-fg'}`}>
                {gap.gapFTE.toFixed(1)} FTE
              </div>
              <div className="text-meta text-ink-500 mt-1">
                {gap.gapFTE > 0 ? `${Math.round(gap.gapFTE / gap.teachersRequired * 100)}% of required staffing` : 'Fully staffed'}
              </div>
            </div>
          </div>
          {qualification && <div className="bg-white p-4 rounded-md border">
              <h5 className="text-body-sm font-medium text-ink-700 mb-2">
                Qualification Requirements
              </h5>
              <div className="text-body-sm text-ink-700 mb-1">
                <span className="font-medium">Required Qualification:</span>{' '}
                {qualification.requiredQualification}
              </div>
              <div className="text-body-sm text-ink-700">
                <span className="font-medium">Special Requirements:</span>{' '}
                {qualification.specialRequirements}
              </div>
            </div>}
          <div>
            <h5 className="text-body-sm font-medium text-ink-700 mb-2">
              Qualified Staff
            </h5>
            {gap.qualifiedStaff.length > 0 ? <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ink-200">
                  <thead className="bg-ink-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                        Teacher
                      </th>
                      <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                        FTE
                      </th>
                      <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                        Utilization
                      </th>
                      <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                        Available FTE
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-ink-200">
                    {gap.qualifiedStaff.map((staff, index) => <tr key={index} className={staff.status === 'leaving' ? 'bg-danger-bg' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-body-sm font-medium text-ink-900">
                            {staff.teacherName}
                          </div>
                          <div className="text-meta text-ink-500 font-mono tabular-nums">
                            {staff.teacherCode}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getTeacherStatusBadge(staff.status)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                          {staff.fte.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                          {staff.utilizationPercent.toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className={`text-body-sm font-medium font-mono tabular-nums ${staff.status === 'leaving' ? 'text-danger-fg' : 'text-ok-fg'}`}>
                            {staff.availableFTE.toFixed(1)}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="bg-danger-bg p-4 rounded-md border border-danger-border">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-danger-fg mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-body-sm font-medium text-danger-fg mb-1">
                      No Qualified Staff
                    </h4>
                    <p className="text-body-sm text-danger-fg">
                      There are no qualified teachers for this subject.
                      Recruitment is critical.
                    </p>
                  </div>
                </div>
              </div>}
          </div>
          <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
              <div>
                <h4 className="text-body-sm font-medium text-navy-800 mb-1">
                  Recruitment Recommendation
                </h4>
                <p className="text-body-sm text-navy-700">
                  {gap.gapFTE <= 0 ? 'This subject is adequately staffed. No immediate recruitment needed.' : gap.gapSeverity === 'critical' ? `Critical staffing shortage of ${gap.gapFTE.toFixed(1)} FTE. Immediate recruitment required for ${gap.subjectName} teacher.` : gap.gapSeverity === 'high' ? `High priority recruitment needed for ${gap.gapFTE.toFixed(1)} FTE ${gap.subjectName} teacher.` : gap.gapSeverity === 'medium' ? `Consider recruiting a ${gap.gapFTE.toFixed(1)} FTE ${gap.subjectName} teacher in the near future.` : `Minor staffing gap of ${gap.gapFTE.toFixed(1)} FTE. Could be addressed through casual staff or internal redistribution.`}
                </p>
                {gap.gapFTE > 0 && <div className="mt-2">
                    <Button variant="secondary" size="sm" onClick={() => {
                  setShowNewRecruitmentModal(true);
                }}>
                      Create Recruitment Need
                    </Button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>;
  };
  // Render recruitment need details
  const renderRecruitmentDetails = (id: string) => {
    const need = recruitmentNeeds.find(n => n.id === id);
    if (!need) return null;
    return <Card className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-body-sm font-medium text-ink-700">
            Recruitment Need: {need.position}
          </h4>
          <div className="flex items-center space-x-2">
            <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
              {need.fte.toFixed(1)} FTE
            </div>
            {getStatusBadge(need.status)}
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Learning Area</div>
              <div className="text-card-title text-ink-900">
                {displayLearningArea(need.department)}
              </div>
              <div className="text-meta text-ink-500 mt-1">
                Priority:{' '}
                {need.priority.charAt(0).toUpperCase() + need.priority.slice(1)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Position</div>
              <div className="text-card-title text-ink-900">
                {need.position}
              </div>
              <div className="text-meta text-ink-500 mt-1">
                FTE: {need.fte.toFixed(1)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Dates</div>
              <div className="text-body-sm text-ink-700">
                Created: {need.createdDate}
              </div>
              {need.approvedDate && <div className="text-body-sm text-ink-700">
                  Approved: {need.approvedDate}
                </div>}
            </div>
          </div>
          <div className="bg-white p-4 rounded-md border">
            <h5 className="text-body-sm font-medium text-ink-700 mb-2">
              Subject Requirements
            </h5>
            <div className="space-y-2">
              {need.subjects.map((subject, index) => <div key={index} className="border-b pb-2 last:border-b-0 last:pb-0">
                  <div className="text-body-sm font-medium text-ink-900">
                    {subject.subjectName} ({subject.subjectCode})
                  </div>
                  <div className="text-meta text-ink-500">
                    Year Levels: {subject.yearLevels.join(', ')}
                  </div>
                </div>)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-md border">
            <h5 className="text-body-sm font-medium text-ink-700 mb-2">Notes</h5>
            <p className="text-body-sm text-ink-700">{need.notes}</p>
          </div>
          <div className="bg-navy-50 p-4 rounded-md border border-navy-200">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
              <div>
                <h4 className="text-body-sm font-medium text-navy-800 mb-1">
                  Recruitment Status
                </h4>
                <p className="text-body-sm text-navy-700">
                  {need.status === 'draft' ? 'This recruitment need is in draft status and needs approval before proceeding.' : need.status === 'approved' ? 'This recruitment need has been approved and is ready for advertising.' : need.status === 'advertised' ? 'This position is currently being advertised. Applications are being accepted.' : need.status === 'interviewing' ? 'Interviews are currently being conducted for this position.' : 'This position has been filled. No further action required.'}
                </p>
                {need.status !== 'filled' && <div className="mt-2 flex space-x-2">
                    {need.status === 'draft' && <Button variant="secondary" size="sm" onClick={() => addToast('Recruitment need approved', 'success')}>
                        Approve
                      </Button>}
                    {need.status === 'approved' && <Button variant="secondary" size="sm" onClick={() => addToast('Position advertised', 'success')}>
                        Advertise Position
                      </Button>}
                    {need.status === 'advertised' && <Button variant="secondary" size="sm" onClick={() => addToast('Status updated to interviewing', 'success')}>
                        Start Interviews
                      </Button>}
                    {need.status === 'interviewing' && <Button variant="secondary" size="sm" onClick={() => addToast('Position marked as filled', 'success')}>
                        Mark as Filled
                      </Button>}
                    <Button variant="secondary" size="sm" onClick={() => addToast('Editing recruitment needs is coming soon', 'info')}>
                      Edit
                    </Button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>;
  };
  // Render new recruitment modal
  const renderNewRecruitmentModal = () => {
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <h3 className="text-section text-ink-900">
                Create New Recruitment Need
              </h3>
              <button className="text-ink-400 hover:text-ink-500" onClick={() => setShowNewRecruitmentModal(false)}>
                &times;
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-body-sm font-medium text-ink-700 mb-1">
                    Learning Area
                  </label>
                  <select className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm">
                    <option value="">Select Learning Area</option>
                    {learningAreaNames.map(name => <option key={name} value={name}>
                        {name}
                      </option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-ink-700 mb-1">
                    Position Title
                  </label>
                  <input type="text" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="e.g. Mathematics Teacher" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-body-sm font-medium text-ink-700 mb-1">
                    FTE
                  </label>
                  <select className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm">
                    <option value="1.0">1.0</option>
                    <option value="0.8">0.8</option>
                    <option value="0.6">0.6</option>
                    <option value="0.5">0.5</option>
                    <option value="0.4">0.4</option>
                    <option value="0.2">0.2</option>
                  </select>
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-ink-700 mb-1">
                    Priority
                  </label>
                  <select className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm">
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-ink-700 mb-1">
                  Subject Requirements
                </label>
                <div className="border rounded-md p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h5 className="text-body-sm font-medium text-ink-700">
                      Subjects
                    </h5>
                    <Button variant="secondary" size="sm" onClick={() => addToast('Subject added', 'success')}>
                      Add Subject
                    </Button>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <select className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm">
                          <option value="">Select Subject</option>
                          {subjectQualifications.map(qual => <option key={qual.id} value={qual.subjectCode}>
                              {qual.subjectName} ({qual.subjectCode})
                            </option>)}
                        </select>
                      </div>
                      <div className="ml-2">
                        <button className="text-danger-fg hover:text-danger-fg">
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="ml-4 mt-2">
                      <label className="block text-meta font-medium text-ink-700 mb-1">
                        Year Levels
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <label className="inline-flex items-center">
                          <input type="checkbox" className="rounded border-ink-300 text-navy-600 focus:ring-navy-500" />
                          <span className="ml-1 text-body-sm text-ink-700">
                            7-10
                          </span>
                        </label>
                        <label className="inline-flex items-center">
                          <input type="checkbox" className="rounded border-ink-300 text-navy-600 focus:ring-navy-500" />
                          <span className="ml-1 text-body-sm text-ink-700">
                            11-12
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-ink-700 mb-1">
                  Notes
                </label>
                <textarea className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" rows={3} placeholder="Additional requirements or information..."></textarea>
              </div>
            </div>
          </div>
          <div className="p-6 border-t">
            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setShowNewRecruitmentModal(false)}>
                Cancel
              </Button>
              <div className="flex space-x-2">
                <Button variant="secondary" onClick={() => {
                addToast('Recruitment need saved as draft', 'success');
                setShowNewRecruitmentModal(false);
              }}>
                  Save as Draft
                </Button>
                <Button variant="primary" onClick={createRecruitmentNeed}>
                  Create & Approve
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div>
      <PageHeader title="Recruitment Gaps" description="Where next year's teaching demand outstrips available staff — plus who's not returning — worked out from the timetable grids and submitted intentions." actions={<div className="flex space-x-2">
            <Button variant="secondary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => loadRealData()} loading={isCalculating}>
              Recalculate
            </Button>
            <Button variant="secondary" leftIcon={<Download className="h-4 w-4" />} onClick={() => addToast('Report downloaded', 'success')}>
              Export Report
            </Button>
            <Button variant="primary" leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setShowNewRecruitmentModal(true)}>
              New Recruitment
            </Button>
          </div>} />
      {isCalculating ? <Card className="p-8">
          <div className="flex justify-center py-8">
            <Spinner size="lg" label="Calculating staffing gaps…" />
          </div>
        </Card> : staffingGaps.length > 0 ? <div className="space-y-6">
          {/* Department Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {departmentSummaries.map((dept, index) => <Card key={index} className="flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-card-title text-ink-900">
                    {displayLearningArea(dept.department)}
                  </h3>
                  <span className={`px-2 py-1 text-meta font-semibold rounded-full ${dept.gapFTE > 0 ? 'bg-danger-bg text-danger-fg' : 'bg-ok-bg text-ok-fg'}`}>
                    {dept.gapFTE > 0 ? `${dept.gapFTE.toFixed(1)} FTE Needed` : 'Fully Staffed'}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-body-sm text-ink-500">Current FTE</div>
                      <div className="text-xl font-medium text-ink-900 font-mono tabular-nums">
                        {dept.currentFTE.toFixed(1)}
                      </div>
                    </div>
                    <div>
                      <div className="text-body-sm text-ink-500">Required FTE</div>
                      <div className="text-xl font-medium text-ink-900 font-mono tabular-nums">
                        {dept.requiredFTE.toFixed(1)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-body-sm text-ink-500">Teachers</div>
                      <div className="text-xl font-medium text-ink-900 font-mono tabular-nums">
                        {dept.teacherCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-body-sm text-ink-500">Leaving</div>
                      <div className={`text-xl font-medium font-mono tabular-nums ${dept.leavingCount > 0 ? 'text-danger-fg' : 'text-ink-900'}`}>
                        {dept.leavingCount}
                      </div>
                    </div>
                  </div>
                  {dept.subjects.some(s => s.gapFTE > 0) && <div className="mt-4 pt-4 border-t">
                      <div className="text-body-sm font-medium text-ink-700 mb-2">
                        Gap Subjects
                      </div>
                      <div className="space-y-2">
                        {dept.subjects.filter(s => s.gapFTE > 0).map((subject, idx) => <div key={idx} className="flex justify-between items-center">
                              <div className="text-body-sm text-ink-700">
                                {subject.subjectName}
                              </div>
                              <div className="text-body-sm font-medium text-danger-fg font-mono tabular-nums">
                                {subject.gapFTE.toFixed(1)} FTE
                              </div>
                            </div>)}
                      </div>
                    </div>}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Button variant="secondary" size="sm" fullWidth onClick={() => setFilterDepartment(dept.department)}>
                    View Details
                  </Button>
                </div>
              </Card>)}
          </div>
          {/* Staffing Gaps */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('gaps')}>
              <h3 className="text-card-title text-ink-900">
                Staffing Gaps
              </h3>
              {expandedSection === 'gaps' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'gaps' && <div className="mt-6 space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <div className="flex-1 flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                    <div className="w-full md:w-64">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-5 w-5 text-ink-400" />
                        </div>
                        <input type="text" className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Search subjects" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <select className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
                        <option value="all">All Learning Areas</option>
                        {learningAreaNames.map(name => <option key={name} value={name}>
                            {name}
                          </option>)}
                      </select>
                      <select className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
                        <option value="all">All Severities</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('subject')}>
                          <div className="flex items-center">
                            <span>Subject</span>
                            {sortField === 'subject' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('department')}>
                          <div className="flex items-center">
                            <span>Learning Area</span>
                            {sortField === 'department' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('teachersRequired')}>
                          <div className="flex items-center">
                            <span>Required FTE</span>
                            {sortField === 'teachersRequired' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('qualifiedTeachers')}>
                          <div className="flex items-center">
                            <span>Qualified</span>
                            {sortField === 'qualifiedTeachers' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('gapFTE')}>
                          <div className="flex items-center">
                            <span>Gap FTE</span>
                            {sortField === 'gapFTE' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('severity')}>
                          <div className="flex items-center">
                            <span>Severity</span>
                            {sortField === 'severity' && <ArrowUpDown className="h-4 w-4 ml-1" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-right text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {getFilteredStaffingGaps().length === 0 ? <tr>
                          <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-center text-body-sm text-ink-500">
                            No staffing gaps found matching the filters
                          </td>
                        </tr> : getFilteredStaffingGaps().map(gap => <tr key={gap.subjectCode} className={`hover:bg-ink-50 ${gap.gapSeverity === 'critical' ? 'bg-danger-bg' : gap.gapSeverity === 'high' ? 'bg-warn-bg' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-body-sm font-medium text-ink-900">
                                {gap.subjectName}
                              </div>
                              <div className="text-meta text-ink-500">
                                {gap.subjectCode}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                              {displayLearningArea(gap.department)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-900 font-mono tabular-nums">
                              {gap.teachersRequired.toFixed(1)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-body-sm text-ink-900 font-mono tabular-nums">
                                {gap.qualifiedTeachers} teachers
                              </div>
                              <div className="text-meta text-ink-500">
                                {gap.qualifiedStaff.filter(s => s.status === 'leaving').length > 0 && <span className="text-danger-fg font-mono tabular-nums">
                                    {gap.qualifiedStaff.filter(s => s.status === 'leaving').length}{' '}
                                    leaving
                                  </span>}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-body-sm font-medium font-mono tabular-nums ${gap.gapFTE > 0 ? 'text-danger-fg' : 'text-ok-fg'}`}>
                                {gap.gapFTE > 0 ? `${gap.gapFTE.toFixed(1)} needed` : 'Fully staffed'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getSeverityBadge(gap.gapSeverity)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-body-sm font-medium">
                              <Button variant="secondary" size="sm" onClick={() => setSelectedGap(gap.subjectCode === selectedGap ? null : gap.subjectCode)}>
                                {gap.subjectCode === selectedGap ? 'Hide Details' : 'View Details'}
                              </Button>
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </div>
                {selectedGap && renderGapDetails(selectedGap)}
              </div>}
          </Card>
          {/* Recruitment Needs */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('recruitment')}>
              <h3 className="text-card-title text-ink-900">
                Recruitment Needs
              </h3>
              {expandedSection === 'recruitment' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'recruitment' && <div className="mt-6 space-y-6">
                <div className="flex justify-between items-center">
                  <div className="text-body-sm text-ink-500">
                    {recruitmentNeeds.length} recruitment needs
                  </div>
                  <Button variant="secondary" size="sm" leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setShowNewRecruitmentModal(true)}>
                    New Recruitment
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Position
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          FTE
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-right text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {getFilteredRecruitmentNeeds().length === 0 ? <tr>
                          <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-center text-body-sm text-ink-500">
                            No recruitment needs recorded yet. Use the staffing gaps and leaving-staff lists above to plan hiring; a recorded needs tracker is coming soon.
                          </td>
                        </tr> : getFilteredRecruitmentNeeds().map(need => <tr key={need.id} className="hover:bg-ink-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-body-sm font-medium text-ink-900">
                                {need.position}
                              </div>
                              <div className="text-meta text-ink-500 font-mono tabular-nums">
                                {need.subjects.map(s => s.subjectCode).join(', ')}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                              {displayLearningArea(need.department)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-900 font-mono tabular-nums">
                              {need.fte.toFixed(1)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getSeverityBadge(need.priority)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(need.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                              {need.createdDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-body-sm font-medium">
                              <Button variant="secondary" size="sm" onClick={() => setSelectedRecruitment(need.id === selectedRecruitment ? null : need.id)}>
                                {need.id === selectedRecruitment ? 'Hide Details' : 'View Details'}
                              </Button>
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </div>
                {selectedRecruitment && renderRecruitmentDetails(selectedRecruitment)}
              </div>}
          </Card>
          {/* Leaving Staff */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('leaving')}>
              <h3 className="text-card-title text-ink-900">
                Leaving Staff
              </h3>
              {expandedSection === 'leaving' ? <ChevronUp className="h-5 w-5 text-ink-500" /> : <ChevronDown className="h-5 w-5 text-ink-500" />}
            </div>
            {expandedSection === 'leaving' && <div className="mt-6 space-y-6">
                <div className="bg-danger-bg p-4 rounded-md border border-danger-border">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-danger-fg mr-3 mt-0.5" />
                    <div>
                      <h4 className="text-body-sm font-medium text-danger-fg mb-1">
                        Staff Leaving
                      </h4>
                      <p className="text-body-sm text-danger-fg">
                        {teacherQualifications.filter(t => t.status === 'leaving').length}{' '}
                        staff members are leaving. This will create gaps in the
                        following subjects:{' '}
                        {Array.from(new Set(teacherQualifications.filter(t => t.status === 'leaving').flatMap(t => t.subjects.map(s => s.subjectName)))).join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Teacher
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          FTE
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Subjects
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Replacement Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {teacherQualifications.filter(t => t.status === 'leaving').length === 0 ? <tr>
                          <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center text-body-sm text-ink-500">
                            No staff members are currently scheduled to leave
                          </td>
                        </tr> : teacherQualifications.filter(t => t.status === 'leaving').map(teacher => <tr key={teacher.id} className="hover:bg-ink-50 bg-danger-bg">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-body-sm font-medium text-ink-900">
                                  {teacher.teacherName}
                                </div>
                                <div className="text-meta text-ink-500 font-mono tabular-nums">
                                  {teacher.teacherCode}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                                {displayLearningArea(teacher.department)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-900 font-mono tabular-nums">
                                {teacher.fte.toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-900 font-mono tabular-nums">
                                {teacher.contractEndDate}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-body-sm text-ink-900">
                                  {teacher.subjects.map(s => s.subjectName).join(', ')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {recruitmentNeeds.some(n => n.notes.toLowerCase().includes(teacher.teacherName.toLowerCase())) ? <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
                                    Replacement Planned
                                  </span> : <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-danger-bg text-danger-fg">
                                    No Replacement
                                  </span>}
                              </td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>}
          </Card>
        </div> : <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">
            No Staffing Gap Data Available
          </h3>
          <p className="text-ink-500 mb-4">
            Staffing gap data has not been calculated yet. Click the
            "Recalculate" button to analyze staffing needs.
          </p>
          <Button variant="primary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => loadRealData()}>
            Calculate Staffing Gaps
          </Button>
        </Card>}
      {/* New Recruitment Modal */}
      {showNewRecruitmentModal && renderNewRecruitmentModal()}
    </div>;
};
export default RecruitmentGaps;
